import React from 'react';
import { X } from 'lucide-react';

export const LegalModal = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;

  const getContent = () => {
    switch (type) {
      case 'privacy':
        return {
          title: 'Privacy Policy',
          content: (
            <>
              <p className="text-sm text-gray-600 mb-6">
                <strong>Last updated:</strong> March 2026
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Information We Collect</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                When you make a booking through our website, we collect personal information including your name, email address, and booking details. This information is necessary to process your reservation and communicate with you about your stay.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2. How We Use Your Information</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Your information is used solely to:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
                <li>Process and confirm your booking</li>
                <li>Communicate with you about your reservation</li>
                <li>Provide you with information about The Tullaghan Pod</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Data Protection & Security</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                All payment information is processed securely through Stripe. We do not store credit card details on our servers. Your personal data is encrypted and stored securely in compliance with GDPR regulations.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Third-Party Services</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                We use Smoobu as our booking management system and Stripe for payment processing. Both services are GDPR-compliant and maintain high security standards. Please refer to their respective privacy policies for more information.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Your Rights</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Under GDPR, you have the right to:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
                <li>Access your personal data</li>
                <li>Request correction of your data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing of your data</li>
                <li>Request transfer of your data</li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                To exercise these rights, please contact us at tullaghanpodco@gmail.com
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Data Retention</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                We retain your booking information for 7 years as required by Irish tax law. After this period, your data is securely deleted unless you have given consent for us to retain it for marketing purposes.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Cookies</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Our website uses essential cookies to ensure proper functionality of the booking system. We do not use tracking or advertising cookies.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Contact</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                For any privacy-related questions or concerns, please contact Caroline and Joe at tullaghanpodco@gmail.com
              </p>
            </>
          )
        };

      case 'terms':
        return {
          title: 'Terms & Conditions',
          content: (
            <>
              <p className="text-sm text-gray-600 mb-6">
                <strong>Last updated:</strong> March 2026
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Booking Agreement</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                By making a booking at The Tullaghan Pod, you agree to these terms and conditions. A booking is confirmed once payment has been received and you have received confirmation from us.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Property Rules</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-2">
                <strong>The Tullaghan Pod is an adults-only property.</strong> We welcome guests aged 18 and over.
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
                <li>Maximum occupancy: 2 guests</li>
                <li>Minimum stay: 1 night</li>
                <li>Maximum stay: 2 nights</li>
                <li>Check-in: 3:00 PM</li>
                <li>Check-out: 11:00 AM</li>
                <li>Closed: Monday, Tuesday, Wednesday</li>
                <li>No smoking inside the pod</li>
                <li>No pets allowed</li>
                <li>No parties or events</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Pricing</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
                <li>Thursday & Sunday: €149 per night</li>
                <li>Friday & Saturday: €159 per night</li>
                <li>All prices include VAT</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Security Deposit</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                A refundable security deposit of €50 per person is required 48 hours before arrival. This is fully returned after your stay once everything is in order. Payment can be made via Revolut or secure online payment link.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Cancellation Policy</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                <strong>Firm Cancellation Policy (Airbnb Standard):</strong>
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
                <li><strong>Full refund:</strong> Cancellations made 30+ days before check-in receive a full refund</li>
                <li><strong>50% refund:</strong> Cancellations made between 7-30 days before check-in receive 50% refund</li>
                <li><strong>No refund:</strong> Cancellations made within 7 days of check-in receive no refund</li>
                <li><strong>No-shows:</strong> No refund for no-shows or early departures</li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                <strong>Important:</strong> This is a strict policy designed to protect the hosts' revenue. The Pod operates with limited availability (closed Mon-Wed, max 2-night stays), making last-minute cancellations particularly impactful. We recommend purchasing travel insurance if your plans are uncertain.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                To cancel your booking, please log into your Smoobu booking confirmation or contact us at tullaghanpodco@gmail.com as soon as possible. Refunds are processed according to the timeline above.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Payment Terms</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Full payment is required at the time of booking. We accept credit cards, debit cards, and payments via Stripe. All payments are processed securely.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Guest Responsibilities</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Guests are responsible for:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
                <li>Treating the property with respect</li>
                <li>Reporting any damage or issues immediately</li>
                <li>Adhering to check-in and check-out times</li>
                <li>Following local regulations and being considerate of neighbors</li>
                <li>Any damage caused during your stay (deducted from security deposit)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Liability</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                While we take every precaution to ensure your safety, The Tullaghan Pod is not liable for any injury, loss, or damage to persons or property during your stay. We recommend travel insurance for all bookings.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Force Majeure</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                We are not liable for failure to perform our obligations due to circumstances beyond our control (e.g., severe weather, natural disasters, government restrictions). In such cases, we will work with you to reschedule or provide a refund.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Contact</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                For any questions about these terms, please contact Caroline and Joe at tullaghanpodco@gmail.com
              </p>
            </>
          )
        };

      case 'cookies':
        return {
          title: 'Cookie Policy',
          content: (
            <>
              <p className="text-sm text-gray-600 mb-6">
                <strong>Last updated:</strong> March 2026
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">1. What Are Cookies?</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and improve your experience.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Cookies We Use</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                <strong>Essential Cookies Only:</strong> Our website uses only essential cookies required for the booking system (Smoobu) to function properly. These cannot be disabled as they are necessary for the website to work.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Third-Party Cookies</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                <strong>Smoobu Booking System:</strong> Our booking widget uses cookies to maintain your session and remember your booking details while you browse availability.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                <strong>Stripe Payment Processing:</strong> Stripe may use cookies to process your payment securely. These are essential for payment security.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4. What We Don't Use</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
                <li>No advertising cookies</li>
                <li>No tracking cookies</li>
                <li>No analytics cookies (e.g., Google Analytics)</li>
                <li>No social media tracking pixels</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Managing Cookies</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                You can control cookies through your browser settings. However, disabling cookies may prevent you from making a booking on our website.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Contact</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                For questions about our cookie policy, contact tullaghanpodco@gmail.com
              </p>
            </>
          )
        };

      default:
        return { title: '', content: null };
    }
  };

  const { title, content } = getContent();

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {content}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-[#2AA7A1] hover:bg-[#238f8a] text-white py-3 px-6 rounded-lg transition-colors duration-200 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
